<!--
 * @Author: Tahiti
 * @Date: 2025-12-10 18:15:30
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-29 16:52:01
 * @Description: 
 * @FilePath: /weryai-web/components/ui/UserProfile.vue
-->
<template>
    <div
        v-if="visible"
        class="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70"
    >
        <div
            class="relative bg-[#22242D] rounded-[12px] p-[30px] w-[90%] md:w-[500px] min-h-[400px] max-w-[500px] mx-4 shadow-2xl"
            @click.stop
        >
            <button
                @click="handleClose"
                class="absolute top-4 right-4 w-6 h-6 flex items-center justify-center text-white hover:rotate-180 transition-all duration-300"
            >
                <img
                    src="@/assets/images/common/_close.webp"
                    alt="close"
                    class="w-[24px]"
                />
            </button>
            <p
                class="text-white text-center text-[20px] md:text-[28px] font-bold"
            >
                {{ t('menus.profile') }}
            </p>
            <div class="flex flex-col gap-[50px]">
                <div>
                    <label
                        for="email"
                        class="text-white text-base font-medium"
                        >{{ t('profiles.email') }}</label
                    >
                    <p class="text-white/60 text-[13px] truncate">
                        {{ userInfo.email }}
                    </p>
                </div>
                <div>
                    <label
                        for="avatar"
                        class="text-white text-base font-medium"
                        >{{ t('profiles.avatar') }}</label
                    >
                    <div class="flex avatar-editor items-center gap-5 mt-5">
                        <img
                            v-if="!singleUploading"
                            :src="userInfo.avatar"
                            alt="avatar"
                            class="w-[102px] h-[102px] rounded-full object-cover"
                        />
                        <div
                            v-else
                            class="flex w-[102px] h-[102px] items-center justify-center"
                        >
                            <l-dot-spinner
                                v-if="isClient"
                                size="40"
                                speed="0.9"
                                color="var(--el-menu-active-color)"
                            ></l-dot-spinner>
                        </div>
                        <button
                            class="flex items-center justify-center rounded-[10px] px-[30px] py-3 text-white text-base border border-[#999] hover:border-text-active hover:text-text-active transition-all duration-300"
                            @click="triggerFileUpload"
                        >
                            {{ t('profiles.editAvatar') }}
                            <input
                                ref="singleFileInput"
                                type="file"
                                accept="image/*"
                                @change="handleSingleFileSelect"
                                class="hidden"
                            />
                        </button>
                    </div>
                </div>
                <div>
                    <label
                        for="nick_name"
                        class="text-white text-base font-medium"
                        >{{ t('profiles.nick_name') }}</label
                    >
                    <div
                        class="relative mt-2 w-full h-[44px] bg-[#333643] rounded-[10px] border border-transparent p-[10px] hover:border-white/30 transition-all duration-300"
                        :class="{ 'border-active-border': isFocused }"
                        :style="{
                            borderColor: isFocused ? '#ffffff4d' : '',
                        }"
                    >
                        <textarea
                            v-model="userInfo.nick_name"
                            rows="1"
                            @focus="isFocused = true"
                            @blur="isFocused = false"
                            class="w-full !p-0 border-none bg-transparent text-white placeholder:gray-300 text-sm focus:outline-none focus:ring-0 focus:border-transparent resize-none"
                        ></textarea>
                    </div>
                </div>
                <button
                    class="generate text-base bg-create-btn w-full disabled:opacity-50 disabled:cursor-not-allowed h-[44px] text-black rounded-[10px] flex items-center justify-center disabled:hover:scale-100 hover:scale-105 transition-all duration-300 cursor-pointer gap-2"
                    :disabled="loading"
                    @click="handleSave"
                >
                    <template v-if="loading">
                        <svg
                            class="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <span>{{ t('profiles.submitting') }}</span>
                    </template>
                    <template v-else>
                        <span>{{ t('profiles.submit') }}</span>
                    </template>
                </button>
            </div>
        </div>
    </div>

    <!-- avatar editor modal -->
    <AvatarEditor
        v-model:visible="avatarEditorVisible"
        :image-src="selectedImageFile"
        @confirm="handleAvatarEditorConfirm"
        @cancel="handleAvatarEditorCancel"
    />
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { updateUserInfoApi } from '~/api/user'
import { noticeMessagePopup, debounce, getMaxImageSize } from '~/utils/common'
import { useUserStore } from '~/stores/user'
import { showLoginModal } from '~/utils/emitter'
import axios from 'axios'
import AvatarEditor from './AvatarEditor.vue'

const userStore = useUserStore()
const { t, locale } = useI18n()
const visible = defineModel<boolean>('visible', { required: true })
const isFocused = ref(false)
const loading = ref(false)

const is_client = import.meta.client
const userInfo = ref({
    nick_name: '',
    email: '',
    avatar: '',
})

const handleClose = () => {
    visible.value = false
}

const singleFileInput = ref<HTMLInputElement | null>(null)
const avatarEditorVisible = ref(false)
const selectedImageFile = ref<File | string | null>(null)

const triggerFileUpload = () => {
    singleFileInput.value?.click()
}

const handleSingleFileSelect = (event: Event) => {
    const target = event.target as HTMLInputElement
    const files = target.files as FileList
    if (!files || files.length === 0) return

    const file = files[0]
    if (!file) return

    // 校验文件大小（限制为10MB）
    if (!getMaxImageSize(file, 10)) {
        noticeMessagePopup(
            t('upload.errors.fileTooLarge', { maxSize: 10 }),
            t('common.tip'),
            'error',
            3000,
            ''
        )
        // 重置 input 值，允许再次选择相同文件
        if (singleFileInput.value) {
            singleFileInput.value.value = ''
        }
        return
    }

    // 打开头像编辑弹窗
    selectedImageFile.value = file
    avatarEditorVisible.value = true

    // 重置 input 值，允许再次选择相同文件
    if (singleFileInput.value) {
        singleFileInput.value.value = ''
    }
}

const singleUploading = ref(false)
// @ts-ignore
const config = useRuntimeConfig()
let token = ''
let language = 'en'

// 将 base64 转换为 Blob
const base64ToBlob = (base64: string): Blob => {
    const arr = base64.split(',')
    const mimeMatch = arr[0]?.match(/:(.*?);/)
    const mime = mimeMatch?.[1] || 'image/png'
    const bstr = atob(arr[1] || '')
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
}

// 上传裁剪后的头像
const uploadCroppedAvatar = async (imageData: string) => {
    singleUploading.value = true
    try {
        // 将 base64 转换为 Blob
        const blob = base64ToBlob(imageData)
        const file = new File([blob], 'avatar.png', { type: 'image/png' })

        // @ts-ignore
        if (import.meta.client) {
            token = localStorage.getItem('token') || ''
            // 请求语言跟随当前 URL 的 locale，而不是偏好/旧的 localStorage('language')
            language = locale.value || 'en'
        }

        const formData = new FormData()
        formData.append('files', file)

        const url = new URL(
            config.public.apiBase + '/api/v1/auth/s3/upload_images'
        )
        url.searchParams.append('app_key', config.public.appKey)

        const res = await axios.post(url.toString(), formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: token,
                'Accept-Language': language,
            },
        })
        const { data, status } = res.data
        if (status === 200) {
            userInfo.value.avatar = data?.object_url_list?.[0]
            singleUploading.value = false
        } else if ([1005, 4001, 4002].includes(status)) {
            showLoginModal()
        }
    } catch (err) {
        console.error('upload failed:', err)
        noticeMessagePopup(
            t('profiles.tip'),
            t('common.tip'),
            'error',
            3000,
            ''
        )
    } finally {
        singleUploading.value = false
    }
}

// 头像编辑确认
const handleAvatarEditorConfirm = async (imageData: string) => {
    await uploadCroppedAvatar(imageData)
    selectedImageFile.value = null
}

// 头像编辑取消
const handleAvatarEditorCancel = () => {
    selectedImageFile.value = null
}

const handleSave = async () => {
    if (loading.value) return
    loading.value = true
    try {
        const { avatar, nick_name } = userInfo.value
        const data_ = {
            avatar,
            nick_name,
        }
        const res: any = await updateUserInfoApi(data_)
        if (res.status === 200) {
            loading.value = false
            userStore.updateUserInfo({
                avatar,
                nick_name,
            })
            noticeMessagePopup(
                t('profiles.submitSuccess'),
                t('profiles.tip'),
                'success',
                5000,
                ''
            )
            handleClose()
        }
    } catch (e) {
        console.log(e)
    } finally {
        loading.value = false
    }
}
const userInfo_ = computed(() => userStore.userInfo)

watch(
    () => userInfo_.value,
    newVal => {
        if (newVal) {
            const { nick_name, email, avatar } = newVal
            userInfo.value = {
                nick_name: nick_name || '',
                email: email || '',
                avatar: avatar || '',
            }
        }
    },
    {
        immediate: true,
    }
)

watch(
    () => visible.value,
    newVal => {
        if (newVal) {
            userStore.getUserInfo(true)
        }
    }
)

const isClient = ref(false)

onMounted(async () => {
    if (typeof window !== 'undefined') {
        try {
            const { dotSpinner } = await import('ldrs')
            dotSpinner.register()
            // 确保注册完成后再设置 isClient，避免组件在注册前渲染
            isClient.value = true
        } catch (error) {
            console.error('Failed to load ldrs:', error)
            // 即使加载失败也设置为 true，避免无限等待
            isClient.value = true
        }
    }
})
</script>

<style scoped lang="less">
.generate {
    &:hover:not(.disabled) {
        transform: translateY(-2px);
        box-shadow: 0 12px 40px rgba(153, 247, 104, 0.5);
    }
}

@media (max-width: 420px) {
    .avatar-editor {
        flex-direction: column;
    }
}
</style>
